import React, { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  genWin2KeepOrderIncludeDouble,
  genWin2KeepOrderNoDouble,
  genWin3KeepOrderIncludeHamTong,
  genWin3KeepOrderNoHamNoTong,
} from '../utils/winNumberUtils'
import { numberSetService, type NumberSetItem } from '../services/numberSetService'
import RulesGuideModal from '../components/RulesGuideModal'

const TYPE_LABEL_TO_PRIZE_CODE: Record<string, string> = {
  '2 ตัวบน': '2_DIGIT_TOP',
  '3 ตัวบน': '3_DIGIT_TOP',
  '3 ตัวโต๊ด': '3_DIGIT_TOD',
  '2 ตัวโต๊ด': '2_DIGIT_TOD',
  '2 ตัวล่าง': '2_DIGIT_BOTTOM',
  '3 ตัวล่าง': '3_DIGIT_BOTTOM',
}

type WinResultItem = { id: string; type: string; number: string }

const PRIZE_TYPE_ORDER = [
  '2 ตัวบน',
  '3 ตัวบน',
  '3 ตัวโต๊ด',
  '2 ตัวโต๊ด',
  '2 ตัวล่าง',
  '3 ตัวล่าง',
] as const

const WinNumberPage: React.FC = () => {
  const navigate = useNavigate()
  const setNameInputRef = useRef<HTMLInputElement>(null)
  const [setName, setSetName] = useState('')
  const [selectedDigits, setSelectedDigits] = useState<number[]>([])
  const [results, setResults] = useState<WinResultItem[]>([])
  const [rulesModalOpen, setRulesModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedWin2, setSelectedWin2] = useState<'no-double' | 'with-double' | null>(null)
  const [selectedWin3, setSelectedWin3] = useState<'no-triple' | 'with-triple' | null>(null)

  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]

  const toggleDigit = (d: number) => {
    setSelectedDigits((prev) => {
      const i = prev.indexOf(d)
      if (i >= 0) return prev.filter((_, idx) => idx !== i)
      return [...prev, d]
    })
  }

  const selectedDisplay = useMemo(() => selectedDigits.join(','), [selectedDigits])
  const canGenerate = selectedDigits.length >= 3
  const hasWinSelection = selectedWin2 != null || selectedWin3 != null

  const addResults = (typeLabel: string, numbers: string[]) => {
    const newItems: WinResultItem[] = numbers.map((number, i) => ({
      id: `win-${typeLabel}-${Date.now()}-${i}-${number}`,
      type: typeLabel,
      number,
    }))
    setResults((prev) => [...prev, ...newItems])
  }

  const handleDrawWinNumbers = () => {
    if (!canGenerate || !hasWinSelection) return
    try {
      if (selectedWin2 === 'no-double') {
        const numbers = genWin2KeepOrderNoDouble(selectedDigits)
        addResults('2 ตัวบน', numbers)
        addResults('2 ตัวโต๊ด', numbers)
        addResults('2 ตัวล่าง', numbers)
      }
      if (selectedWin2 === 'with-double') {
        const numbers = genWin2KeepOrderIncludeDouble(selectedDigits)
        addResults('2 ตัวบน', numbers)
        addResults('2 ตัวโต๊ด', numbers)
        addResults('2 ตัวล่าง', numbers)
      }
      if (selectedWin3 === 'no-triple') {
        const numbers = genWin3KeepOrderNoHamNoTong(selectedDigits)
        addResults('3 ตัวบน', numbers)
        addResults('3 ตัวโต๊ด', numbers)
        addResults('3 ตัวล่าง', numbers)
      }
      if (selectedWin3 === 'with-triple') {
        const numbers = genWin3KeepOrderIncludeHamTong(selectedDigits)
        addResults('3 ตัวบน', numbers)
        addResults('3 ตัวโต๊ด', numbers)
        addResults('3 ตัวล่าง', numbers)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'เกิดข้อผิดพลาด', { position: 'top-center' })
    }
  }

  const groupByType = useMemo(() => {
    const map = new Map<string, WinResultItem[]>()
    results.forEach((r) => {
      const list = map.get(r.type) ?? []
      list.push(r)
      map.set(r.type, list)
    })
    return PRIZE_TYPE_ORDER.map((typeLabel) => ({
      typeLabel,
      items: map.get(typeLabel) ?? [],
    }))
  }, [results])

  const deleteByType = (typeLabel: string) => {
    setResults((prev) => prev.filter((r) => r.type !== typeLabel))
  }

  const removeResult = (id: string) => {
    setResults((prev) => prev.filter((r) => r.id !== id))
  }

  const clearAll = () => {
    setResults([])
    setSelectedDigits([])
    setSelectedWin2(null)
    setSelectedWin3(null)
    setSetName('')
  }

  const handleSaveData = async () => {
    const name = setName.trim()
    if (!name) {
      toast.error('กรุณาระบุชื่อเลขชุด', { position: 'top-center' })
      setTimeout(() => setNameInputRef.current?.focus(), 100)
      return
    }
    if (results.length === 0) {
      toast.error('กรุณาจับวินตัวเลขก่อน', { position: 'top-center' })
      return
    }

    const items: NumberSetItem[] = groupByType
      .filter((g) => g.items.length > 0)
      .map((g) => ({
        prizeTypeCode: TYPE_LABEL_TO_PRIZE_CODE[g.typeLabel] ?? g.typeLabel,
        typeLabel: g.typeLabel,
        numbers: g.items.map((i) => i.number).join(', '),
      }))

    try {
      setSaving(true)
      await numberSetService.create({ name, items })
      toast.success('บันทึกเลขชุดเรียบร้อยแล้ว', { position: 'top-center' })
      navigate('/number-set')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกเลขชุดไม่สำเร็จ', { position: 'top-center' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/number-set')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      จัดการเลขชุด
                    </a>
                    <a
                      href="#"
                      className="txt-link-d bdr-5 bgg-4 px-2 c-white"
                      onClick={(e) => {
                        e.preventDefault()
                        setRulesModalOpen(true)
                      }}
                    >
                      กติกา & วิธีดูผล
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-random me-2"></i>
                      จับวินเลข
                    </div>

                    <div className="row mx--1">
                      <div className="col-12 col-lg-6 px-1 mb-2">
                        <div className="bdr-5 bgc-4 c-dark p-2 h-100">
                          <div className="wrap t-medium mb-2">
                            <i className="fas fa-edit me-2"></i>
                            ชื่อเลขชุด
                          </div>
                          <input
                            ref={setNameInputRef}
                            type="text"
                            className="w-100 border-0 bgc-7 bdr-5 p-2 mb-2"
                            placeholder="ชื่อเลขชุด"
                            value={setName}
                            onChange={(e) => setSetName(e.target.value)}
                          />

                          <div className="bdr-5 bgc-7 p-2 mb-2 text-center">
                            <small className="c-dark d-block mb-1">รายการที่เลือก</small>
                            <h4 className="c-red mb-0">{selectedDisplay || '-'}</h4>
                          </div>

                          <div className="bdr-5 bgc-7 p-2 mb-2">
                            <p className="c-dark text-center mb-2">
                              กรุณาเลือกตัวเลขอย่างน้อย 3 ตัวขึ้นไป
                            </p>
                            <div className="row mx--1" style={{ rowGap: '0.35rem' }}>
                              {digits.map((d) => {
                                const isActive = selectedDigits.includes(d)
                                return (
                                  <div
                                    key={d}
                                    className="px-1"
                                    style={{ flex: '0 0 20%', maxWidth: '20%' }}
                                  >
                                    <button
                                      type="button"
                                      className={`w-100 border-0 bdr-5 py-2 ${isActive ? 'bgg-4 c-white' : 'bgc-4 c-red'}`}
                                      onClick={() => toggleDigit(d)}
                                    >
                                      <strong>{d}</strong>
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          </div>

                          <div className="row mx--1" style={{ rowGap: '0.5rem' }}>
                            <div className="col-12 col-md-6 px-1">
                              <button
                                type="button"
                                className={`w-100 border-0 bdr-5 py-2 ${selectedWin2 === 'no-double' ? 'bgg-4 c-white' : 'bgc-4 c-red'}`}
                                onClick={() => canGenerate && setSelectedWin2((prev) => (prev === 'no-double' ? null : 'no-double'))}
                              >
                                จับวิน 2 ตัวไม่รวมเบิ้ล
                              </button>
                            </div>
                            <div className="col-12 col-md-6 px-1">
                              <button
                                type="button"
                                className={`w-100 border-0 bdr-5 py-2 ${selectedWin2 === 'with-double' ? 'bgg-4 c-white' : 'bgc-4 c-red'}`}
                                onClick={() => canGenerate && setSelectedWin2((prev) => (prev === 'with-double' ? null : 'with-double'))}
                              >
                                จับวิน 2 ตัวรวมเบิ้ล
                              </button>
                            </div>
                            <div className="col-12 col-md-6 px-1">
                              <button
                                type="button"
                                className={`w-100 border-0 bdr-5 py-2 ${selectedWin3 === 'no-triple' ? 'bgg-4 c-white' : 'bgc-4 c-red'}`}
                                onClick={() => canGenerate && setSelectedWin3((prev) => (prev === 'no-triple' ? null : 'no-triple'))}
                              >
                                จับวิน 3 ตัวไม่รวมหามตอง
                              </button>
                            </div>
                            <div className="col-12 col-md-6 px-1">
                              <button
                                type="button"
                                className={`w-100 border-0 bdr-5 py-2 ${selectedWin3 === 'with-triple' ? 'bgg-4 c-white' : 'bgc-4 c-red'}`}
                                onClick={() => canGenerate && setSelectedWin3((prev) => (prev === 'with-triple' ? null : 'with-triple'))}
                              >
                                จับวิน 3 ตัวรวมหามตอง
                              </button>
                            </div>
                            <div className="col-12 px-1">
                              <button
                                type="button"
                                className="check-button bgg-1 bdc-1 c-white w-100"
                                disabled={!canGenerate || !hasWinSelection}
                                onClick={handleDrawWinNumbers}
                              >
                                <p className="">
                                  <i className="fas fa-magic me-2"></i>
                                  จับวินตัวเลข
                                </p>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 px-1 mb-2">
                        <div className="bdr-5 bgc-4 c-dark p-2 h-100">
                          <div className="wrap t-medium mb-2">
                            <i className="fas fa-list me-2"></i>
                            รายการทั้งหมด
                          </div>

                          {results.length === 0 ? (
                            <div className="text-center py-4 bdr-5 bgc-7 c-dark">
                              ยังไม่มีรายการจับวิน เลือกตัวเลขแล้วกดปุ่มจับวินด้านซ้าย
                            </div>
                          ) : (
                            <div className="d-grid gap-2">
                              {groupByType.map(({ typeLabel, items }) => (
                                <div key={typeLabel}>
                                  <div className="wrap justify-content-between bdr-5 bgc-7 px-2 py-1 mb-1">
                                    <strong className="c-red">{typeLabel}</strong>
                                    <button
                                      type="button"
                                      className="bgg-1 bdr-5 py-1 px-2 c-white border-0"
                                      disabled={items.length === 0}
                                      onClick={() => deleteByType(typeLabel)}
                                    >
                                      <i className="fas fa-trash-alt me-1"></i>
                                      ลบชนิดนี้ทั้งหมด
                                    </button>
                                  </div>
                                  <div className="table-responsive w-100">
                                    <table className="table table-set mb-0">
                                      <tbody>
                                        {items.length === 0 ? (
                                          <tr>
                                            <td colSpan={4} className="text-center c-dark py-2">ไม่มีรายการ</td>
                                          </tr>
                                        ) : (
                                          items.map((row, idx) => (
                                            <tr key={row.id}>
                                              <th scope="row" className="text-center">{idx + 1}</th>
                                              <td><p className="bgc-7 c-dark bdr-5 p-2">{row.type}</p></td>
                                              <td><p className="bgc-7 c-dark bdr-5 p-2">{row.number}</p></td>
                                              <td>
                                                <button
                                                  type="button"
                                                  className="bgg-1 bdr-5 py-2 c-white w-100 border-0"
                                                  onClick={() => removeResult(row.id)}
                                                >
                                                  <i className="fas fa-trash-alt"></i>
                                                </button>
                                              </td>
                                            </tr>
                                          ))
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="row mx--1 mt-2">
                            <div className="col-12 px-1 mb-2">
                              <button
                                type="button"
                                className="check-button bgc-3 c-white w-100 border-0"
                                onClick={clearAll}
                              >
                                <p className="">
                                  <i className="fas fa-eraser me-2"></i>
                                  ล้างเลขจับวินทั้งหมด
                                </p>
                              </button>
                            </div>
                            <div className="col-12 px-1">
                              <button
                                type="button"
                                className="check-button bgg-4 c-white w-100 border-0"
                                disabled={saving || results.length === 0}
                                onClick={handleSaveData}
                              >
                                <p className="">
                                  {saving ? (
                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                  ) : (
                                    <i className="fas fa-check-circle me-2"></i>
                                  )}
                                  {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
                                </p>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RulesGuideModal
        isOpen={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        fallbackRules={(
          <>
            <p>- ระบบเปิด-ปิดแทงตามเวลาที่ได้แจ้งไว้ และบริษัทสามารถพิจารณาปิดแทงก่อนกำหนดได้</p>
            <p>- ทางบริษัทสามารถคืนเลขได้ทุกกรณี แต่จะคืนก่อนที่จะออกผลรางวัล</p>
            <p>- การคืนเลขหรือคืนโพยของสมาชิก ต้องเป็นไปเงื่อนไขและเวลาที่ทางเว็บกำหนดเท่านั้น</p>
            <p>- ราคาจ่ายและส่วนลดจะลดลงตามเลขดังหรือเลขอั้น ให้สมาชิกตรวจสอบโพยและราคาจ่าย/ส่วนลดหลังการแทงทุกครั้ง</p>
            <p>- กรุณาเลือกตัวเลขอย่างน้อย 3 ตัวขึ้นไปก่อนกดจับวิน</p>
            <p>- ระบบจะสร้างเลขวินตามชนิดที่เลือก และบันทึกเป็นเลขชุดของลูกค้า</p>
          </>
        )}
        fallbackGuides={(
          <>
            <p className="mt-3"><u><strong>วิธีการดูผลรางวัล 'หวยรัฐบาลไทย'</strong></u></p>
            <div className="mt-3 mb-4 text-center">
              <p className="mb-1">ใช้รางวัลที่ 1 ในการออกผลรางวัล เช่น 199606</p>
              <p className="mb-1">สามตัวบน (606) สองตัวล่าง (94)</p>
              <p className="mb-1">สามตัวล่าง (220,173,388,094)</p>
            </div>
            <p><u><strong>การตรวจผลรางวัล</strong></u></p>
            <table className="table table-bordered mb-0">
              <tbody>
                <tr><td className="text-nowrap" style={{ width: 100 }}>3 ตัวบน</td><td>606</td></tr>
                <tr><td className="text-nowrap">2 ตัวบน</td><td>06</td></tr>
                <tr><td className="text-nowrap">2 ตัวโต๊ด</td><td>94 49 06 60</td></tr>
                <tr><td className="text-nowrap">3 ตัวโต๊ด</td><td>606 660 066</td></tr>
                <tr><td className="text-nowrap">วิ่งบน</td><td>6 - 0 - 6</td></tr>
                <tr><td className="text-nowrap">ปักหลักร้อย</td><td>6</td></tr>
                <tr><td className="text-nowrap">ปักหลักสิบ</td><td>0</td></tr>
                <tr><td className="text-nowrap">ปักหลักหน่วย</td><td>6</td></tr>
                <tr><td className="text-nowrap">2 ตัวล่าง</td><td>94</td></tr>
                <tr><td className="text-nowrap">วิ่งล่าง</td><td>4 - 9</td></tr>
                <tr><td className="text-nowrap">4 ตัวบน</td><td>9606</td></tr>
                <tr><td className="text-nowrap">4 ตัวโต๊ด</td><td>9606 สลับตำแหน่งทั้ง 4 หลัก</td></tr>
                <tr><td className="text-nowrap">5 ตัวโต๊ด</td><td>99606 สลับตำแหน่งทั้ง 5 หลัก</td></tr>
              </tbody>
            </table>
          </>
        )}
      />
    </div>
  )
}

export default WinNumberPage
